import React, { useState, useEffect } from "react";
import { Form, Row, Col } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { uploadImage } from "../../utils/helpers/fileupload";
import { getSubjectTeacher } from "../../store/slices/subjectteacher";
import { getNote, updateNote } from "../../store/slices/notes";
import {
  FiHome,
  FiChevronRight,
} from "react-icons/fi";

const requiredMessages = {
  standardId: "Please select standard",
  subjectId: "Please select subject",
  chapter: "Please enter chapter name",
};

export default function EditNotes() {
  const { id } = useParams();

  const user = JSON.parse(localStorage.getItem("user"));
  const token = user?.data?.accessToken;
  const teacherId = user?.data?.teacherId;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [rawData, setRawData] = useState([]);
  const [standards, setStandards] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [errors, setErrors] = useState({});
  const [file, setFile] = useState(null);
  const [existingFile, setExistingFile] = useState("");

  const [formData, setFormData] = useState({
    standardId: "",
    subjectId: "",
    chapter: "",
  });

  /* ---------- Fetch teacher mapping ---------- */
  useEffect(() => {
    const fetchTeacherData = async () => {
      const res = await dispatch(getSubjectTeacher(teacherId)).unwrap();
      const data = res?.data || [];

      setRawData(data);

      const uniqueStandards = [
        ...new Map(
          data.map((i) => [i.standardId._id, i.standardId])
        ).values(),
      ];

      setStandards(uniqueStandards);
    };

    fetchTeacherData();
  }, [dispatch, teacherId]);

  /* ---------- Fetch note by id  <<<<< ---------- */
  useEffect(() => {
    const fetchNoteById = async () => {
      try {
        const res = await dispatch(getNote({ id, teacherId })).unwrap();
        const notedata = Array.isArray(res?.data) ? res.data[0] : res?.data;

        if (!notedata) {
          toast.error("Note not found");
          return;
        }

        const standardId =
          notedata.standardId?._id || notedata.standardId || "";
        const subjectId =
          notedata.subjectId?._id || notedata.subjectId || "";

        setFormData({
          standardId,
          subjectId,
          chapter: notedata.chapter || "",
        });

        setExistingFile(notedata.file || "");

        const sub = rawData
          .filter((i) => i.standardId._id === standardId)
          .map((i) => i.subjectId);

        setSubjects([...new Map(sub.map((s) => [s._id, s])).values()]);
      } catch {
        toast.error("Failed to load note details");
      }
    };

    if (rawData.length && id) {
      fetchNoteById();
    }
  }, [dispatch, id, rawData, teacherId]);

  const handleStandardChange = (e) => {
    const standardId = e.target.value;

    setFormData((prev) => ({
      ...prev,
      standardId,
      subjectId: "",
    }));

    // Clear validation errors
    setErrors((prev) => ({
      ...prev,
      standardId: "",
      subjectId: "",
    }));

    const filteredSubjects = rawData
      .filter((item) => item.standardId._id === standardId)
      .map((item) => item.subjectId);

    const uniqueSubjects = [
      ...new Map(filteredSubjects.map((sub) => [sub._id, sub])).values(),
    ];

    setSubjects(uniqueSubjects);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value || "" }));
    setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
    ];

    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error("Only PDF, JPG, PNG files allowed");
      e.target.value = "";
      return;
    }

    if (selectedFile.size > 2 * 1024 * 1024) {
      toast.error("File must be under 2MB");
      e.target.value = "";
      return;
    }

    setFile(selectedFile);
    setExistingFile("");

    setErrors((prev) => ({
      ...prev,
      file: "",
    }));
  };

  const validateForm = () => {
    let err = {};
    Object.keys(requiredMessages).forEach((k) => {
      if (!formData[k]) err[k] = requiredMessages[k];
    });
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  /* --->>> Submit ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      let fileUrl = existingFile;
      if (file) {
        fileUrl = await uploadImage(file, token);
      }

      const payload = {
        id,
        teacherId,
        ...formData,
        file: fileUrl,
      };

      await dispatch(updateNote(payload)).unwrap();
      toast.success("Notes updated successfully");
      navigate("/teacher/notes");
    } catch (err) {
      toast.error(err || "Failed to update notes");
    }
  };

  return (
    <>

      <div className="page-header">
        <div className="breadcrumb-section">

          <div className="breadcrumb-left">
            <div className="home-icon">
              <Link to="/teacher/home"><FiHome style={{ color: "white", marginTop: "-4px" }} /></Link>
            </div>

            <FiChevronRight className="separator" />

            <Link to="/teacher/notes">Notes</Link>

            <FiChevronRight className="separator" />

            <span className="active">Edit Note</span>
          </div>

        </div>
      </div>
      <div className="acs-card">
        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col md={4}>
              <Form.Label>Standard</Form.Label>
              <Form.Select name="standardId"
                value={formData.standardId}
                onChange={handleStandardChange}
              >
                <option value="">Select Standard</option>
                {standards.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name}
                  </option>
                ))}
              </Form.Select>
              {errors.standardId && (
                <small className="text-danger">{errors.standardId}</small>
              )}
            </Col>

            <Col md={4}>
              <Form.Label>Subject</Form.Label>
              <Form.Select
                name="subjectId"
                value={formData.subjectId}
                onChange={handleChange}
              >
                <option value="">Select Subject</option>
                {subjects.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name}
                  </option>
                ))}
              </Form.Select>
              {errors.subjectId && (
                <small className="text-danger">{errors.subjectId}</small>
              )}
            </Col>

            <Col md={4}>
              <Form.Label>Chapter</Form.Label>
              <Form.Control
                name="chapter"
                value={formData.chapter}
                onChange={handleChange} placeholder="Enter chapter name"
              />
              {errors.chapter && (
                <small className="text-danger">{errors.chapter}</small>
              )}
            </Col>

            <Col md={6}>
              <Form.Label>Upload File</Form.Label>


              <Form.Control
                type="file"
                accept=".pdf,.jpg,.png"
                onChange={handleFileChange}
              />
              {existingFile && !file && (
                <div className="mb-1">
                  <small>Existing file:</small>{" "}
                  <a
                    href={existingFile}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: "12px", textDecoration: "none" }}
                  >
                    📄 View file
                  </a>
                </div>
              )}
            </Col>
          </Row>

          <div className="an-actions mt-4">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button type="submit" className="btn-submit">
              Update
            </button>
          </div>
        </Form>

      </div>
    </>
  );
}
